import classNames from 'classnames'
import { FC, useRef, useState } from 'react'
import { ReactComponent as CheckIcon } from 'src/assets/svg/check.svg'
import { ReactComponent as DoubleCheckIcon } from 'src/assets/svg/double-check-white.svg'
import { AiToolIcon } from 'src/components/AiToolIcon'
import useOutsideClick from 'src/hooks/useOutsideClick'
import styles from './aiModelMultiSelect.module.scss'

export const AI_MODEL_OPTIONS = [
  'ChatGPT',
  'Claude',
  'Gemini',
  'Perplexity',
] as const

export type AiModelOption = (typeof AI_MODEL_OPTIONS)[number]

interface AiModelMultiSelectProps {
  className?: string
  // Shown as a plain field the user cannot open (e.g. a competitor measured
  // on its brand's models).
  disabled?: boolean
  hasError?: boolean
  id?: string
  onChange?: (value: AiModelOption[]) => void
  placeholder?: string
  value: readonly string[]
}

// The AI model picker of the AI Listening forms: an "All AI tools" toggle and
// one checkbox row per model with its logo.
export const AiModelMultiSelect: FC<AiModelMultiSelectProps> = ({
  className,
  disabled = false,
  hasError = false,
  id,
  onChange,
  placeholder = 'Choose ChatGPT, Claude, Gemini, or Perplexity',
  value,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useOutsideClick({ onOutsideClick: () => setIsOpen(false), ref: wrapperRef })

  // Kept in the canonical order whatever order they were picked in.
  const selected = AI_MODEL_OPTIONS.filter((option) => value.includes(option))
  const isAllSelected = selected.length === AI_MODEL_OPTIONS.length

  const toggleAll = () => onChange?.(isAllSelected ? [] : [...AI_MODEL_OPTIONS])

  const toggle = (option: AiModelOption) =>
    onChange?.(
      selected.includes(option)
        ? selected.filter((item) => item !== option)
        : AI_MODEL_OPTIONS.filter(
            (item) => item === option || selected.includes(item)
          )
    )

  return (
    <div className={classNames(styles.wrapper, className)} ref={wrapperRef}>
      <button
        aria-expanded={isOpen}
        className={classNames(styles.field, {
          [styles.fieldOpen]: isOpen,
          [styles.fieldError]: hasError,
          [styles.fieldDisabled]: disabled,
        })}
        disabled={disabled}
        id={id}
        onClick={() => setIsOpen((open) => !open)}
        type="button"
      >
        <span
          className={classNames(styles.fieldText, {
            [styles.placeholder]: selected.length === 0,
          })}
        >
          {selected.length === 0 ? placeholder : selected.join(', ')}
        </span>
        {!disabled && (
          <svg
            className={classNames(styles.chevron, {
              [styles.chevronOpen]: isOpen,
            })}
            fill="none"
            height="20"
            viewBox="0 0 20 20"
            width="20"
          >
            <path
              d="M5 7.5L10 12.5L15 7.5"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.8"
            />
          </svg>
        )}
      </button>

      {isOpen && !disabled && (
        <div className={styles.menu} role="listbox">
          <button className={styles.item} onClick={toggleAll} type="button">
            <span
              className={classNames(styles.checkbox, {
                [styles.checkboxAllChecked]: isAllSelected,
              })}
            >
              {isAllSelected && <DoubleCheckIcon />}
            </span>
            <span className={styles.itemName}>All AI tools</span>
          </button>
          {AI_MODEL_OPTIONS.map((option) => {
            const isChecked = selected.includes(option)
            return (
              <button
                aria-selected={isChecked}
                className={styles.item}
                key={option}
                onClick={() => toggle(option)}
                role="option"
                type="button"
              >
                <span
                  className={classNames(styles.checkbox, {
                    [styles.checkboxChecked]: isChecked,
                  })}
                >
                  {isChecked && <CheckIcon className={styles.checkIcon} />}
                </span>
                <span className={styles.itemName}>{option}</span>
                <AiToolIcon className={styles.itemIcon} name={option} />
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
